// createBaseApiClient.ts

import { log } from '@/utils';

import { Cache } from './cache';
import { ExtractPathParams, fillPathParams, toQueryString } from './helpers';
import { ApiContracts, ApiResponse } from './types';

interface ApiRequestOptions<T = any> {
  data?: T;
  headers?: Record<string, string>;
  useCache?: boolean;
  cacheTtl?: number; // ms
}

interface BaseApiClientOptions {
  store_hash: string;
  baseUrl: string;
  cacheTtl?: number;
  cachePrefix?: string;
  defaultHeaders?: Record<string, string>;
}

export function createBaseApiClient({
  store_hash,
  cachePrefix,
  cacheTtl,
  baseUrl,
  defaultHeaders = {},
}: BaseApiClientOptions) {
  const CACHE_PREFIX = cachePrefix ?? 'its_cache_';
  const DEFAULT_CACHE_TTL = cacheTtl ?? 5 * 60 * 1000;

  async function request<
    Path extends keyof ApiContracts,
    Method extends keyof ApiContracts[Path] & string,
    Payload extends ApiContracts[Path][Method] extends { payload: any }
      ? ApiContracts[Path][Method]['payload']
      : undefined,
    Response extends ApiContracts[Path][Method] extends { response: any }
      ? ApiContracts[Path][Method]['response']
      : undefined,
  >(
    endpoint: Path,
    {
      method,
      data: initialPayload,
      pathParams,
      headers = {},
      useCache = false,
      cacheTtl = DEFAULT_CACHE_TTL,
    }: ApiRequestOptions<Payload> & {
      method: Method;
      pathParams?: Record<ExtractPathParams<Path>, string | number>;
    },
  ): Promise<Response | ApiResponse<null>> {
    try {
      const isGet = method === 'GET';
      const data = { ...initialPayload, store_hash };

      let url = `${baseUrl}${
        pathParams ? fillPathParams(endpoint, pathParams) : endpoint
      }`;

      if (isGet && Object.keys(data).length > 0) {
        url += `?${new URLSearchParams(data).toString()}`;
      }

      const finalHeaders = {
        'Content-Type': 'application/json',
        ...defaultHeaders,
        ...headers,
      };

      const cacheKey = await Cache.makeKey<Payload>(
        CACHE_PREFIX,
        endpoint,
        initialPayload,
      );

      if (isGet && useCache) {
        const cached = Cache.get<Response>(cacheKey);
        if (cached) {
          log.info('Returning cached data.');
          return cached;
        }
      }

      const finalUrl = isGet ? url : `${url}?store_hash=${store_hash}`;

      const response = await fetch(finalUrl, {
        method,
        headers: finalHeaders,
        body: isGet ? undefined : toQueryString(data),
      });

      if (!response.ok) {
        throw new Error(
          `API request failed: ${response.status} ${response.statusText}`,
        );
      }

      const json = await response.json();

      if (isGet && useCache) {
        Cache.set<Response>(cacheKey, json, cacheTtl);
      }

      return json;
    } catch (error: any) {
      log.error('API request error', { error });
      return {
        data: null,
        meta: {
          status: 'error',
          message: error.message || 'Unknown error has occurred',
        },
      };
    }
  }

  return {
    request,
    cleanAllCache: () => Cache.cleanAll(CACHE_PREFIX),
    cleanExpiredCache: () => Cache.cleanExpired(CACHE_PREFIX),
  };
}
